import type { InvoiceActivityChange } from "@/apis/gql/types";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { useGetInvoiceActivity } from "@/queries/invoices/activity";
import { formatDistance } from "date-fns";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Pencil, Upload } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { useGetAllSuppliers } from "@/queries/suppliers/get-all";

import {
  ExportFailedIcon,
  ExportedIcon,
  ProcessingIcon,
  ReviewedIcon,
  ToReviewIcon,
} from "@/components/shared/status/icons";
import { Separator } from "@/components/ui/separator";
import { TypographyMuted, TypographySmall } from "@/components/ui/typography";

const INCLUDED_PATHS = [
  "invoiceNo",
  "isNettoInvoice",
  "invoiceDate",
  "totalAmount",
  "currency",
  "ocrReviewed",
  "supplierId",
  "deletedAt",
  "paid",
  "split",
  "status",
  "reviewType",
  "reviewTypeOverwritten",
  "serviceDate",
  "processingStatus",
  "validationErrors",
] as const;

type Props = {
  invoiceId: string;
};

export const InvoiceActivity = ({ invoiceId }: Props) => {
  const { t } = useTranslation();
  const { data: suppliers } = useGetAllSuppliers();

  const { invoiceActivity } = useGetInvoiceActivity({
    invoiceId,
  });

  const enhanceValue = (field: string, value: string | null) => {
    if (field === "supplierId") {
      return suppliers.find((supplier) => supplier.supplierId === value)
        ?.supplierName;
    }

    if (
      value &&
      (field === "invoiceDate" ||
        field === "serviceDate" ||
        field === "deletedAt")
    ) {
      return format(new Date(value), "dd.MM.yyyy");
    }

    return value;
  };

  const _activity = invoiceActivity.map((activity) => {
    if (activity.activityType === "create") {
      return {
        id: activity.invoiceActivityId,
        user: activity.user.name,
        createdAt: activity.createdAt,
        icon: <Upload size={11} />,
        changes: [
          {
            translationKey: "activity.created",
            value: t("invoice"),
          },
        ],
      };
    }

    const changes = activity.changes.filter((change) =>
      INCLUDED_PATHS.some((excludedPath) => change.path.includes(excludedPath)),
    );

    return {
      id: activity.invoiceActivityId,
      user: activity.user.name,
      createdAt: activity.createdAt,
      icon: getIcon(changes),

      changes: changes.map((change) => {
        let changeType = "";

        if (change.oldValue && change.value) {
          changeType = "change";
        } else if (change.oldValue) {
          changeType = "removed";
        } else {
          changeType = "added";
        }

        return {
          translationKey: `activity.${changeType}`,
          field: change.path.join("."),
          oldValue: enhanceValue(change.path.join("."), change.oldValue),
          value: enhanceValue(change.path.join("."), change.value),
        };
      }),
    };
  });

  const validActivity = _activity.filter(
    (activity) => activity.changes.length > 0,
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      <TypographySmall>Activity</TypographySmall>
      <div className="py-2 px-4 w-full">
        {validActivity.map((activity, index) => (
          <div key={activity.id} className="flex flex-row gap-2 w-full">
            <div className="flex flex-col">
              <div className="w-4 h-4 bg-muted rounded-full flex items-center justify-center">
                {activity.icon}
              </div>
              {index !== validActivity.length - 1 && (
                <div className="flex flex-1 justify-center w-4 my-1">
                  <Separator orientation="vertical" />
                </div>
              )}
            </div>
            <div className="flex-1 min-h-[25px] mb-2">
              <div className="grid w-full items-center gap-4">
                <div className="flex flex-col space-y-1">
                  {activity.changes.map((change, index) => {
                    return (
                      <div
                        key={index.toString()}
                        className={cn(
                          index > 0 &&
                            index === activity.changes.length - 1 &&
                            "pb-2",
                        )}
                      >
                        <TypographyMuted className="text-xs">
                          <span className="font-semibold text-primary">
                            {`${activity.user} `}
                          </span>
                          <Trans
                            i18nKey={change.translationKey as any}
                            values={{
                              // @ts-ignore
                              field: change.field,
                              // @ts-ignore
                              oldValue: change.oldValue,
                              value: change.value,
                            }}
                            components={{
                              Bold: (
                                <span className="font-semibold text-primary" />
                              ),
                            }}
                          />
                          <span> · </span>
                          <span>
                            {formatDistance(
                              new Date(),
                              new Date(activity.createdAt),
                              {
                                locale: de,
                              },
                            )}
                          </span>
                        </TypographyMuted>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const getIcon = (changes: InvoiceActivityChange[]) => {
  if (changes.some((change) => change.path.includes("status"))) {
    const status = changes.find((change) =>
      change.path.includes("status"),
    )?.value;

    switch (status) {
      case InvoiceStatus.REVIEWED:
        return <ReviewedIcon />;
      case InvoiceStatus.EXPORTING:
        return <ProcessingIcon />;
      case InvoiceStatus.EXPORT_FAILED:
        return <ExportFailedIcon />;
      case InvoiceStatus.EXPORTED:
        return <ExportedIcon />;
      case InvoiceStatus.NEW:
        return <ProcessingIcon />;
      case InvoiceStatus.PREPROCESSED:
        return <ToReviewIcon />;
      default:
        return <Pencil size={11} />;
    }
  }

  return <Pencil size={11} />;
};
